import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, BarChart3, FlaskConical, Layers, Plus, RefreshCw, Trash2, Tv } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { adminApi } from "../../api/admin";
import {
  Badge,
  Banner,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Modal,
  PageHeader,
  SearchInput,
  Skeleton,
  Textarea,
  useConfirm,
  useToast,
} from "../../ds";
import type { Device, DeviceGroup } from "../../types";
import { deviceLabel, errorText } from "./labels";

const SUGGESTIONS = ["Board room", "Reception", "CEO office", "Management floor", "Operations", "Commercial"];

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

function GroupCard({ group, devices, reports, onView, onApprove, onDelete }: { group: DeviceGroup; devices: Device[]; reports: string[] | undefined; onView: () => void; onApprove: () => void; onDelete: () => void }) {
  const active = devices.filter((d) => d.status === "active");
  const off = devices.filter((d) => d.status === "disabled" || d.status === "revoked").length;
  const names = active.map(deviceLabel).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  return (
    <Card>
      <div className="ds-row" style={{ alignItems: "flex-start", gap: 12 }}>
        <span className="ds-stat__icon" style={{ width: 40, height: 40, borderRadius: 10 }}>
          <Layers size={19} />
        </span>
        <div className="ds-grow" style={{ minWidth: 0 }}>
          <div className="ds-row" style={{ gap: 8, minWidth: 0 }}>
            <span className="ds-truncate" style={{ fontWeight: 650, fontSize: 15 }} title={group.name}>
              {group.name}
            </span>
            {group.name === "Laptops" ? (
              <Badge tone="accent" title="Builders' laptops: they count as screens and show the live draft, even before publishing.">
                <FlaskConical size={11} /> Test group
              </Badge>
            ) : null}
          </div>
          <div className="ds-small ds-muted" style={{ marginTop: 2, minHeight: 18 }}>
            {group.description?.trim() || <span className="ds-faint">No description</span>}
          </div>
        </div>
      </div>

      <div className="ds-row" style={{ marginTop: 16, gap: 6, flexWrap: "wrap" }}>
        <Badge tone={active.length ? "success" : "neutral"} dot>
          {plural(active.length, "active screen")}
        </Badge>
        {off ? <Badge title="Disabled or revoked TVs in this group">{off} switched off</Badge> : null}
        {reports ? <Badge tone={reports.length ? "info" : "neutral"}>{plural(reports.length, "report")}</Badge> : null}
      </div>

      <div className="ds-small ds-muted" style={{ marginTop: 12, display: "grid", gap: 6 }}>
        <span className="ds-row" style={{ gap: 6, alignItems: "flex-start" }}>
          <Tv size={14} style={{ marginTop: 2, flexShrink: 0 }} />
          <span>{names.length ? names.slice(0, 3).join(", ") + (names.length > 3 ? ` and ${names.length - 3} more` : "") : "No TVs yet — pick this group when you approve a TV."}</span>
        </span>
        {reports ? (
          <span className="ds-row" style={{ gap: 6, alignItems: "flex-start" }}>
            <BarChart3 size={14} style={{ marginTop: 2, flexShrink: 0 }} />
            <span>{reports.length ? reports.slice(0, 2).join(", ") + (reports.length > 2 ? ` and ${reports.length - 2} more` : "") : "No reports shared with this group yet."}</span>
          </span>
        ) : null}
      </div>

      <div className="ds-row" style={{ marginTop: 16, justifyContent: "flex-end" }}>
        {devices.length === 0 ? (
          <Button size="sm" variant="ghost" icon={Trash2} onClick={onDelete} title="Only an empty group can be deleted">
            Delete group
          </Button>
        ) : null}
        {devices.length ? (
          <Button size="sm" variant="ghost" iconRight={ArrowRight} onClick={onView}>
            View screens
          </Button>
        ) : (
          <Button size="sm" variant="ghost" iconRight={ArrowRight} onClick={onApprove}>
            Approve a TV
          </Button>
        )}
      </div>
    </Card>
  );
}

function NewGroupModal({ open, onClose, existing }: { open: boolean; onClose: () => void; existing: DeviceGroup[] }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName("");
      setDescription("");
      setError(null);
    }
  }, [open]);

  const taken = new Set(existing.map((g) => g.name.trim().toLowerCase()));
  const duplicate = taken.has(name.trim().toLowerCase());
  const suggestions = SUGGESTIONS.filter((s) => !taken.has(s.toLowerCase()));

  const create = useMutation({
    mutationFn: () => adminApi.createDeviceGroup({ name: name.trim(), description: description.trim() || undefined }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "device-groups"] });
      toast.success("Screen group created", `Next, approve a TV into “${name.trim()}” from the TV screens page.`);
      onClose();
    },
    onError: (e) => setError(errorText(e, "The group couldn't be created.")),
  });
  const canSave = !!name.trim() && !duplicate && !create.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New screen group"
      description="Name it after a place or a team. You choose a group when you approve a TV, and share reports with the whole group at once."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" icon={Plus} loading={create.isPending} disabled={!canSave} onClick={() => create.mutate()}>
            Create group
          </Button>
        </>
      }
    >
      <div className="ds-stack">
        <Field label="Group name" error={duplicate ? "A group with this name already exists." : error ?? undefined}>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Board room" maxLength={120} onKeyDown={(e) => e.key === "Enter" && canSave && create.mutate()} />
        </Field>
        {suggestions.length && !name ? (
          <div className="ds-row" style={{ flexWrap: "wrap", gap: 6, marginTop: -6 }}>
            <span className="ds-small ds-faint">Ideas:</span>
            {suggestions.map((s) => (
              <Button key={s} size="sm" variant="subtle" onClick={() => setName(s)}>
                {s}
              </Button>
            ))}
          </div>
        ) : null}
        <Field label="Description" hint="Optional — e.g. which floor or room the TVs are in.">
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} style={{ minHeight: 64 }} maxLength={500} />
        </Field>
      </div>
    </Modal>
  );
}

export function DeviceGroupsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const groups = useQuery({ queryKey: ["admin", "device-groups"], queryFn: adminApi.listDeviceGroups });
  const devices = useQuery({ queryKey: ["admin", "devices"], queryFn: adminApi.listDevices });
  const reports = useQuery({ queryKey: ["admin", "reports"], queryFn: adminApi.listReports });
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");

  const byGroup = useMemo(() => {
    const m = new Map<string, Device[]>();
    for (const d of devices.data ?? []) {
      if (!d.device_group_id) continue;
      m.set(d.device_group_id, [...(m.get(d.device_group_id) ?? []), d]);
    }
    return m;
  }, [devices.data]);

  const reportsByGroup = useMemo(() => {
    if (!reports.data) return null;
    const m = new Map<string, string[]>();
    for (const r of reports.data) {
      if (r.status !== "active" || !r.current_published_version_id) continue;
      for (const g of r.device_group_names ?? []) m.set(g, [...(m.get(g) ?? []), r.name]);
    }
    return m;
  }, [reports.data]);

  const list = useMemo(() => {
    const q = search.trim().toLowerCase();
    return [...(groups.data ?? [])]
      .filter((g) => !q || g.name.toLowerCase().includes(q) || (g.description ?? "").toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  }, [groups.data, search]);

  const totalActive = (devices.data ?? []).filter((d) => d.status === "active").length;

  const removeGroup = useMutation({
    mutationFn: (g: DeviceGroup) => adminApi.deleteDeviceGroup(g.id),
    onSuccess: (_r, g) => {
      void qc.invalidateQueries({ queryKey: ["admin", "device-groups"] });
      void qc.invalidateQueries({ queryKey: ["admin", "reports"] });
      void qc.invalidateQueries({ queryKey: ["reports"] });
      toast.success("Screen group deleted", `“${g.name}” was removed.`);
    },
    onError: (e) => toast.error("Couldn't delete the group", errorText(e)),
  });
  const confirmRemove = async (g: DeviceGroup) => {
    const shared = reportsByGroup?.get(g.name) ?? [];
    if (
      await confirm({
        title: `Delete “${g.name}”?`,
        message: shared.length
          ? `It has no screens. ${plural(shared.length, "report")} shared with this group (${shared.slice(0, 3).join(", ")}${shared.length > 3 ? "…" : ""}) lose this group from their screen list.`
          : "It has no screens and no reports are shared with it.",
        confirmLabel: "Delete group",
        tone: "danger",
      })
    )
      removeGroup.mutate(g);
  };

  return (
    <div className="ds-page">
      <PageHeader
        title="Screen groups"
        subtitle="Group TVs by place or team. Share a report with a group and every TV in it shows that report."
        actions={
          <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
            New group
          </Button>
        }
      />

      {groups.isError ? (
        <Banner tone="danger" title="Screen groups couldn't be loaded" action={<Button size="sm" icon={RefreshCw} onClick={() => void groups.refetch()}>Try again</Button>}>
          {errorText(groups.error)}
        </Banner>
      ) : groups.isLoading ? (
        <div className="ds-grid ds-grid--auto">
          {[0, 1, 2].map((i) => (
            <Card key={i}>
              <Skeleton width="55%" height={16} />
              <Skeleton width="80%" style={{ marginTop: 10 }} />
              <Skeleton width="40%" height={22} style={{ marginTop: 18 }} />
              <Skeleton width="90%" style={{ marginTop: 14 }} />
            </Card>
          ))}
        </div>
      ) : (groups.data ?? []).length === 0 ? (
        <Card>
          <EmptyState
            icon={Layers}
            title="No screen groups yet"
            description="Create a group for each place with TVs — for example Board room or Reception. You'll pick a group when you approve a TV."
            action={
              <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
                Create your first group
              </Button>
            }
          />
        </Card>
      ) : (
        <>
          <div className="ds-toolbar">
            {(groups.data ?? []).length > 6 ? <SearchInput value={search} onChange={setSearch} placeholder="Search groups" /> : null}
            <span className="ds-small ds-muted">
              {plural((groups.data ?? []).length, "group")}
              {devices.data ? ` · ${plural(totalActive, "active screen")} in total` : ""}
            </span>
          </div>
          {devices.isError ? (
            <div style={{ marginBottom: 16 }}>
              <Banner tone="warning" title="Screen counts aren't available">
                The list of TVs couldn't be loaded, so each group shows 0 screens for now.
              </Banner>
            </div>
          ) : null}
          {list.length === 0 ? (
            <Card>
              <EmptyState icon={Layers} title="No groups match" description="Try another search." action={<Button onClick={() => setSearch("")}>Clear search</Button>} />
            </Card>
          ) : (
            <div className="ds-grid ds-grid--auto" style={{ alignItems: "start" }}>
              {list.map((g) => (
                <GroupCard
                  key={g.id}
                  group={g}
                  devices={byGroup.get(g.id) ?? []}
                  reports={reportsByGroup ? reportsByGroup.get(g.name) ?? [] : undefined}
                  onView={() => navigate(`/admin/devices?group=${encodeURIComponent(g.id)}`)}
                  onApprove={() => navigate("/admin/devices")}
                  onDelete={() => void confirmRemove(g)}
                />
              ))}
            </div>
          )}
        </>
      )}

      <NewGroupModal open={creating} onClose={() => setCreating(false)} existing={groups.data ?? []} />
    </div>
  );
}
